import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Boxes } from "lucide-react";

import {
  listServices,
  listServicesByDomain,
} from "@/lib/api-explorer/load-specs";
import { OTHER_DOMAIN, type ServiceMeta } from "@/lib/api-explorer/types";

export const Route = createFileRoute("/_authenticated/api-explorer/")({
  component: ApiExplorerIndex,
});

function ApiExplorerIndex() {
  const grouped = listServicesByDomain();
  const totalCount = listServices().length;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">API Explorer</h2>
        <p className="text-muted-foreground text-sm">
          Try Nike services from the browser. Requests are proxied through
          compass-auth — your Compass JWT proves who you are; the proxy attaches
          whatever service token the upstream needs.
        </p>
      </div>

      {totalCount === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col gap-8">
          {grouped.map(({ domain, services }) => (
            <DomainSection key={domain} domain={domain} services={services} />
          ))}
        </div>
      )}
    </div>
  );
}

function DomainSection({
  domain,
  services,
}: {
  domain: string;
  services: ServiceMeta[];
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between border-b pb-2">
        <h3 className="text-lg font-semibold tracking-tight">
          {formatDomain(domain)}
        </h3>
        <span className="text-muted-foreground text-xs">
          {services.length} {services.length === 1 ? "service" : "services"}
        </span>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {services.map((s) => (
          <ServiceCard key={s.id} meta={s} />
        ))}
      </div>
    </section>
  );
}

function ServiceCard({ meta }: { meta: ServiceMeta }) {
  return (
    <Link
      to="/api-explorer/$service"
      params={{ service: meta.id }}
      className="bg-card text-card-foreground hover:border-primary/40 group flex flex-col gap-2 rounded-lg border p-4 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Boxes className="text-muted-foreground size-4" />
          <span className="font-medium">{meta.title}</span>
        </div>
        <ArrowRight className="text-muted-foreground group-hover:text-foreground size-4 transition-colors" />
      </div>
      {meta.description && (
        <p className="text-muted-foreground line-clamp-3 text-sm whitespace-pre-line">
          {meta.description}
        </p>
      )}
      <p className="text-muted-foreground/70 mt-1 font-mono text-xs">
        /auth/proxy/{meta.id}
      </p>
    </Link>
  );
}

function formatDomain(domain: string): string {
  if (domain === OTHER_DOMAIN) return "Other";
  // Treat the directory name as the canonical id (lowercase, no spaces)
  // and just title-case the first letter for display. Keeps the rendering
  // stable even when someone names a domain `recsys` vs `RecSys`.
  return domain.charAt(0).toUpperCase() + domain.slice(1);
}

function EmptyState() {
  return (
    <div className="bg-muted/40 text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
      No services registered yet. Drop an OpenAPI document at{" "}
      <code className="bg-muted rounded px-1">
        api-specs/&lt;domain&gt;/&lt;id&gt;/api.yaml
      </code>{" "}
      and add a matching entry to the proxy registry in compass-auth.
    </div>
  );
}
